import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';
import { useNativeGoogleSignIn } from '../hooks/useNativeGoogleSignIn';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [requiresTermsAcceptance, setRequiresTermsAcceptance] = useState(false);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
          api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

          // Validate the token by making a test API call
          try {
            console.log('🔍 Validating stored token...');
            const termsResponse = await api.get('/terms/check');
            setRequiresTermsAcceptance(!termsResponse.data.hasAccepted);
            console.log('✅ Token is valid');
          } catch (error) {
            console.error('❌ Token validation failed:', error.response?.status);
            // If token is invalid (401/403), clear it and force re-login
            if (error.response?.status === 401 || error.response?.status === 403) {
              console.log('🔄 Clearing invalid token and forcing re-login');
              logout();
              // Show a toast message if we're not on a public page
              if (!['/login', '/register', '/'].includes(window.location.pathname)) {
                import('react-hot-toast').then(({ default: toast }) => {
                  toast.error('Your session has expired. Please sign in again.');
                });
              }
            } else {
              // For other errors, assume they need to accept terms
              setRequiresTermsAcceptance(true);
            }
          }
        } catch (error) {
          console.error('Error parsing stored user data:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { user: userData, token: userToken, requiresTermsAcceptance: needsTerms } = response.data;
      
      setUser(userData);
      setToken(userToken);
      setRequiresTermsAcceptance(needsTerms || false);
      
      localStorage.setItem('token', userToken);
      localStorage.setItem('user', JSON.stringify(userData));
      api.defaults.headers.common['Authorization'] = `Bearer ${userToken}`;
      
      return { success: true, requiresTermsAcceptance: needsTerms || false };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const { user: newUser, token: userToken, requiresTermsAcceptance: needsTerms } = response.data;
      
      setUser(newUser);
      setToken(userToken);
      setRequiresTermsAcceptance(needsTerms || false);
      
      localStorage.setItem('token', userToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      api.defaults.headers.common['Authorization'] = `Bearer ${userToken}`;
      
      return { success: true, requiresTermsAcceptance: needsTerms || false };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Registration failed' 
      };
    }
  };

  const googleLogin = async (credential) => {
    console.log('🔐 AuthContext.googleLogin: Starting Google login...');
    console.log('🔑 Credential length:', credential?.length);

    try {
      console.log('📡 AuthContext.googleLogin: Calling API /auth/google/verify...');

      const response = await api.post('/auth/google/verify', {
        credential
      });

      console.log('📦 AuthContext.googleLogin: API response received:', response.data);

      const { user: userData, token: userToken, isNewUser, requiresTermsAcceptance: needsTerms } = response.data;

      console.log('👤 User data:', userData);
      console.log('🔑 Token received:', userToken ? 'Yes' : 'No');
      console.log('🆕 Is new user:', isNewUser);
      console.log('📋 Requires terms:', needsTerms);

      setUser(userData);
      setToken(userToken);
      setRequiresTermsAcceptance(needsTerms || false);

      localStorage.setItem('token', userToken);
      localStorage.setItem('user', JSON.stringify(userData));
      api.defaults.headers.common['Authorization'] = `Bearer ${userToken}`;

      console.log('✅ AuthContext.googleLogin: Login successful!');
      console.log('🎯 User state updated, navigation should trigger');

      return { success: true, isNewUser, requiresTermsAcceptance: needsTerms || false };
    } catch (error) {
      console.error('❌ AuthContext.googleLogin: Error during Google login:', error);
      console.error('📡 API Error response:', error.response?.data);

      return {
        success: false,
        error: error.response?.data?.error || 'Google sign-in failed'
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
  };

  const acceptTerms = async () => {
    try {
      // Get latest terms
      const termsResponse = await api.get('/terms/latest');
      const latestTerms = termsResponse.data;
      
      // Accept the terms
      await api.post('/terms/accept', { termsId: latestTerms.id });
      
      setRequiresTermsAcceptance(false);
      return { success: true };
    } catch (error) {
      console.error('Failed to accept terms:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to accept terms' 
      };
    }
  };

  // Set up native Google Sign-In listener
  useNativeGoogleSignIn(googleLogin);

  const value = {
    user,
    token,
    login,
    register,
    googleLogin,
    logout,
    loading,
    requiresTermsAcceptance,
    acceptTerms
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};